import { useState, useEffect } from 'react';

const percentToPx = (percent, total) => {
  const decimal = parseInt(percent.replace('%', '')) / 100;
  return total * decimal;
};

const pxStringToInt = (px) => parseInt(px.replace('px', ''));

const isPercent = (val) => val.match(/^[0-9]{1,3}%$/g);
const isPx = (val) => val.match(/^[0-9]px$/g);

const getRefWidthHeight = (ref) => {
  if (ref?.current) {
    return [ref.current.offsetWidth, ref.current.offsetHeight];
  }
  return [0, 0];
};

const getPositionFromPercentage = (value, containerWidth, itemWidth) => {
  const percentContainerWidth = percentToPx(value, containerWidth);
  const percentItemWidth = percentToPx(value, itemWidth);
  return (percentItemWidth - percentContainerWidth) * -1;
};

const sanitizePosition = (position, container, dragItem) => {
  const { x, y } = position;
  const { containerWidth, containerHeight } = container;
  const { dragItemWidth, dragItemHeight } = dragItem;
  let posX = x;
  let posY = y;
  if (typeof x === 'string') {
    if (isPercent(x))
      posX = getPositionFromPercentage(x, containerWidth, dragItemWidth);
    else if (isPx(x)) posX = pxStringToInt(x);
    else posX = 0;
  }
  if (typeof y === 'string') {
    if (isPercent(y))
      posY = getPositionFromPercentage(y, containerHeight, dragItemHeight);
    else if (isPx(y)) posY = pxStringToInt(y);
    else posY = 0;
  }

  return { x: posX, y: posY };
};

const useDragWithin = (
  dragItemRef,
  containerRef,
  initialPosition,
  condition = true
) => {
  const positionReset = { x: null, y: null };
  const [pristine, setPristine] = useState(true);
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const [position, setPosition] = useState(positionReset);
  const [startPosition, setStartPosition] = useState(positionReset);

  useEffect(() => {
    const [containerWidth, containerHeight] = getRefWidthHeight(containerRef);
    const [dragItemWidth, dragItemHeight] = getRefWidthHeight(dragItemRef);
    const hasDimensions =
      containerWidth && dragItemWidth && containerHeight && dragItemHeight;
    if (pristine && hasDimensions && initialPosition) {
      const sanitized = sanitizePosition(
        initialPosition,
        { containerWidth, containerHeight },
        { dragItemWidth, dragItemHeight }
      );
      if (position.x !== sanitized.x || position.y !== sanitized.y)
        setPosition(sanitized);
    }
  }, [containerRef, dragItemRef, initialPosition, pristine]);

  const overridePosition = (newPosition) => {
    const [containerWidth, containerHeight] = getRefWidthHeight(containerRef);
    const [dragItemWidth, dragItemHeight] = getRefWidthHeight(dragItemRef);

    const sanitized = sanitizePosition(
      newPosition,
      { containerWidth, containerHeight },
      { dragItemWidth, dragItemHeight }
    );
    setPosition(sanitized);
  };

  const handleMouseDown = (event) => {
    if (condition) {
      const { clientX, clientY } = event;
      setStartPosition({ x: clientX, y: clientY });
      if (pristine) setPristine(false);
    }
  };

  const getValidPosition = (xDrag, yDrag) => {
    const [containerWidth, containerHeight] = getRefWidthHeight(containerRef);
    const [dragItemWidth, dragItemHeight] = getRefWidthHeight(dragItemRef);
    const { x, y } = position;
    const maxX = -(dragItemWidth - containerWidth);
    const maxY = -(dragItemHeight - containerHeight);
    const newX = x + xDrag;
    const newY = y + yDrag;
    let validX = newX;
    let validY = newY;
    if (newX > 0) validX = 0;
    if (newY > 0) validY = 0;
    if (newX < maxX) validX = maxX;
    if (newY < maxY) validY = maxY;

    return { x: validX, y: validY };
  };

  const handleMouseMove = (event) => {
    const { clientX, clientY } = event;
    const { x: startX, y: startY } = startPosition;
    if (condition && mouseIsDown && startX && startY) {
      const xDragAmount = clientX - startX;
      const yDragAmount = clientY - startY;
      const updatedPosition = getValidPosition(xDragAmount, yDragAmount);
      setPosition(updatedPosition);
    }
  };

  useEffect(() => {
    const removeMouseMove = () =>
      document.removeEventListener('mousemove', handleMouseMove, false);

    if (condition && mouseIsDown)
      document.addEventListener('mousemove', handleMouseMove, false);
    else removeMouseMove();

    return removeMouseMove;
  }, [condition, mouseIsDown]);

  const documentMouseDown = () => setMouseIsDown(true);
  const documentMouseUp = () => {
    setMouseIsDown(false);
    setStartPosition(positionReset);
  };

  useEffect(() => {
    const removeListeners = () => {
      document.removeEventListener('mousedown', documentMouseDown, false);
      document.removeEventListener('mouseup', documentMouseUp, false);
    };

    if (condition) {
      document.addEventListener('mousedown', documentMouseDown, false);
      document.addEventListener('mouseup', documentMouseUp, false);
    } else {
      removeListeners();
    }

    return removeListeners;
  }, [condition]);

  return [position, handleMouseDown, overridePosition];
};

export default useDragWithin;
